import { BreadcrumbGroup } from '@cloudscape-design/components';
import useGetProductById from 'features/products/hooks/useGetProductById';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Unified breadcrumbs for the Products + Inventory section.
 *
 * Route → trail
 * /products                               Home › Inventory › Products
 * /products/:id                           Home › Inventory › Products › LNP-001
 * /inventory/:productDocId/:versionId     Home › Inventory › Products › LNP-001 › Inventory · v2
 * /inventory                              Home › Products › Inventory
 */
export default function ProductInventoryBreadcrumbs({ pathnames }) {
  const navigate = useNavigate();

  const isInventory = pathnames[0] === 'inventory';
  const productDocId = pathnames[1];
  const versionId = isInventory ? pathnames[2] : null;
  const isReconcile = isInventory && pathnames[3] === 'reconcile';

  const { data: product } = useGetProductById(productDocId ?? null);
  const productLabel = [product?.sku, product?.name].filter(Boolean).join(' - ') || productDocId;

  const items = useMemo(() => {
    if (!isInventory) {
      // /products  or  /products/:id
      const crumbs = [
        { text: 'Home', href: '/' },
        { text: 'Inventory', href: '/inventory' },
        { text: 'Products', href: '/products' },
      ];
      if (productDocId) {
        crumbs.push({
          text: productLabel,
          href: `/products/${productDocId}`,
        });
      }
      return crumbs;
    }

    // /inventory  or  /inventory/:productDocId/:versionId
    if (!productDocId || !versionId) {
      return [
        { text: 'Home', href: '/' },
        { text: 'Products', href: '/products' },
        { text: 'Inventory', href: '/inventory' },
      ];
    }

    // Detail: /inventory/:productDocId/:versionId
    const crumbs = [
      { text: 'Home', href: '/' },
      { text: 'Inventory', href: '/inventory' },
      { text: 'Products', href: '/products' },
    ];
    crumbs.push({
      text: productLabel,
      href: `/products/${productDocId}`,
    });
    crumbs.push({
      text: `Inventory · ${versionId}`,
      href: `/inventory/${productDocId}/${versionId}`,
    });
    if (isReconcile) {
      crumbs.push({
        text: 'Reconciliation',
        href: `/inventory/${productDocId}/${versionId}/reconcile`,
      });
    }
    return crumbs;
  }, [isInventory, isReconcile, productDocId, versionId, productLabel]);

  return (
    <BreadcrumbGroup
      items={items}
      onClick={(e) => {
        e.preventDefault();
        if (e.detail.href) navigate(e.detail.href);
      }}
    />
  );
}

ProductInventoryBreadcrumbs.propTypes = {
  pathnames: PropTypes.arrayOf(PropTypes.string).isRequired,
};
